// Header dropdown for About / Documentation / Issues / Report Issue / Contact.
// Mirrors the desktop hamburger menu's role from CLAUDE.local.md design notes.
// Renders the ReportIssueModal as a sibling so the dropdown can close
// while the modal stays open.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ReportIssueModal } from './ReportIssueModal';
import { useIssuesEnabled, useQrosCliDownloads } from '../lib/usePages';
import { canFileIssue, useMe } from '../lib/useMe';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

// Public-facing repo for cloud users — they have no GitHub access to the
// private dev repo (TauruSystems/tres). Issues + filed reports live here so the
// user can see them. Documentation is EMBEDDED in the app (see /docs), never
// linked out to GitHub.
const PUBLIC_REPO = 'https://github.com/TauruSystems/TReS_public';
const ISSUES_URL = `${PUBLIC_REPO}/issues`;
const CONTACT_EMAIL = 'info@taurusystems.com';

interface AboutMenuProps {
  /** App version from /api/health. Optional — header may not have it yet. */
  version?: string | undefined;
}

export function AboutMenu({ version }: AboutMenuProps) {
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { data: qrosCli } = useQrosCliDownloads();
  const { data: issuesEnabled } = useIssuesEnabled();
  const me = useMe();
  // A guest is refused the reporter, so the entry is not offered to one.
  const mayReport = issuesEnabled === true && canFileIssue(me.data);

  // Outside click + Escape close the dropdown (but not the modal — the
  // modal manages its own dismissal).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <div ref={wrapperRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cn(buttonVariants({ variant: 'chrome', size: 'md' }), 'inline-flex h-7 w-7 items-center justify-center')}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="About TReS"
          title="About TReS"
        >
          ?
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 top-9 z-40 w-64 rounded-md border border-border bg-card text-tres-text-primary shadow-lg"
          >
            <div className="border-b border-border px-4 py-3">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold">TReS</span>
                <span className="text-xs text-tres-text-primary">LLM Grounding Platform</span>
              </div>
              {version && (
                <div className="mt-0.5 font-mono text-xs text-tres-text-secondary">
                  v{version}
                </div>
              )}
            </div>

            <div className="py-1 text-sm">
              <Link
                role="menuitem"
                to="/preferences"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                Preferences
              </Link>
              <Link
                role="menuitem"
                to="/docs/changelog"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                What&rsquo;s New
              </Link>
              <Link
                role="menuitem"
                to="/docs/user-guide"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                Documentation
              </Link>
              <Link
                role="menuitem"
                to="/docs/learn-about-qros"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                Learn About QROS
              </Link>
              <Link
                role="menuitem"
                to="/docs/querying-with-qql"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                Querying with QQL
              </Link>
              <Link
                role="menuitem"
                to="/docs/terms-of-service"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                Terms of Service
              </Link>
              <a
                role="menuitem"
                href={ISSUES_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 hover:bg-muted"
              >
                Browse known issues
              </a>
              {mayReport && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    setReportOpen(true);
                  }}
                  className="block w-full px-4 py-2 text-left hover:bg-muted"
                >
                  Report an issue…
                </button>
              )}
            </div>

            {qrosCli && (
              <div className="border-t border-border py-1 text-sm">
                <div className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
                  qros CLI
                </div>
                <div className="px-4 pb-1 text-xs text-tres-text-secondary">
                  Prep &amp; validate files locally before loading (convert, lint).
                </div>
                <a
                  role="menuitem"
                  href={qrosCli.macos_arm64}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2 hover:bg-muted"
                >
                  Download for macOS (Apple Silicon)
                </a>
                <a
                  role="menuitem"
                  href={qrosCli.windows_x64}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2 hover:bg-muted"
                >
                  Download for Windows (x64)
                </a>
              </div>
            )}

            <div className="border-t border-border px-4 py-2 text-xs text-tres-text-secondary">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-tres-action hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        )}
      </div>

      <ReportIssueModal open={reportOpen} onClose={() => setReportOpen(false)} />
    </>
  );
}
